<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="560"
    persistent
    overlay-color="#0f172a"
    overlay-opacity="0.65"
    content-class="ism-cropper-dialog"
  >
    <div class="ism-cropper-wrap">

      <!-- Header -->
      <div class="ism-cropper-header">
        <span class="ism-cropper-title">{{ title }}</span>
        <button class="ism-cropper-close" @click="cancel" type="button">
          <v-icon size="18" color="#64748b">mdi-close</v-icon>
        </button>
      </div>

      <!-- Cropper canvas -->
      <div class="ism-cropper-body">
        <Cropper
          ref="cropper"
          class="ism-cropper-canvas"
          :src="imageSrc"
          :stencil-size="stencilSize"
          :stencil-props="stencilProps"
          :stencil-component="stencilComponent"
          background-class="ism-cropper-bg"
          :image-restriction="imageRestriction"
        />
      </div>

      <!-- Controls: zoom / rotate / flip -->
      <div class="ism-cropper-controls">
        <button class="ism-crop-ctrl-btn" @click="zoom(-0.1)" type="button" title="Zoom Out">
          <v-icon size="18">mdi-magnify-minus-outline</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="zoom(0.1)"  type="button" title="Zoom In">
          <v-icon size="18">mdi-magnify-plus-outline</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="rotate(-90)" type="button" title="Rotate Left">
          <v-icon size="18">mdi-rotate-left</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="rotate(90)"  type="button" title="Rotate Right">
          <v-icon size="18">mdi-rotate-right</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="flip(true, false)"  type="button" title="Flip Horizontal">
          <v-icon size="18">mdi-flip-horizontal</v-icon>
        </button>
        <button class="ism-crop-ctrl-btn" @click="flip(false, true)" type="button" title="Flip Vertical">
          <v-icon size="18">mdi-flip-vertical</v-icon>
        </button>
      </div>

      <!-- Footer -->
      <div class="ism-cropper-footer">
        <button class="ism-crop-btn-cancel" @click="cancel" type="button">Cancel</button>
        <button class="ism-crop-btn-apply"  @click="applyCrop" type="button">
          <v-icon size="15" style="margin-right:5px;">mdi-crop</v-icon>
          Apply Crop
        </button>
      </div>

    </div>
  </v-dialog>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: 'ImageCropper',
  components: { Cropper },

  props: {
    value:      { type: Boolean, default: false },
    imageSrc:   { type: String,  default: '' },
    title:      { type: String,  default: 'Crop Image' },
    // 'circle' for profile pics, 'square' for logos
    shape:      { type: String,  default: 'square', validator: v => ['circle', 'square'].includes(v) },
    outputSize: { type: Number,  default: 400 },
  },

  computed: {
    stencilComponent() {
      return this.shape === 'circle' ? 'circle-stencil' : 'rectangle-stencil';
    },

    stencilProps() {
      const common = { aspectRatio: 1 };
      return this.shape === 'circle'
        ? common
        : { ...common, movable: false, resizable: false, handlers: {}, lines: {} };
    },

    stencilSize() {
      if (this.shape !== 'square') return null;
      return ({ boundaries }) => {
        const size = Math.min(300, Math.floor(Math.min(boundaries.width, boundaries.height) * 0.78));
        return { width: size, height: size };
      };
    },

    imageRestriction() {
      return this.shape === 'square' ? 'none' : 'stencil';
    },
  },

  methods: {
    zoom(delta)    { this.$refs.cropper.zoom(1 + delta); },
    rotate(deg)    { this.$refs.cropper.rotate(deg); },
    flip(h, v)     { this.$refs.cropper.flip(h, v); },
    cancel()       { this.$emit('input', false); },

    applyCrop() {
      const { canvas } = this.$refs.cropper.getResult();
      if (!canvas) return;

      const size   = this.outputSize;
      const resized = document.createElement('canvas');
      resized.width  = size;
      resized.height = size;
      resized.getContext('2d').drawImage(canvas, 0, 0, size, size);

      resized.toBlob(blob => {
        if (!blob) return;
        const file       = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        this.$emit('cropped', { file, previewUrl });
        this.$emit('input', false);
      }, 'image/jpeg', 0.92);
    },
  },
};
</script>

<!-- global: target v-dialog content-class -->
<style>
.ism-cropper-dialog {
  border-radius: 18px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.3) !important;
}
</style>

<style scoped>
/* ── Wrapper ─────────────────────────────── */
.ism-cropper-wrap {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  font-family: "Inter", sans-serif;
}

/* ── Header ──────────────────────────────── */
.ism-cropper-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  background: #ffffff;
  border-bottom: 1px solid #eef2f7;
  flex-shrink: 0;
}
.ism-cropper-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.01em;
}
.ism-cropper-close {
  width: 30px; height: 30px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s;
}
.ism-cropper-close:hover { background: #f1f5f9; }

/* ── Cropper canvas ──────────────────────── */
.ism-cropper-body {
  background: #000000;
  height: 340px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ism-cropper-canvas {
  width: 100%;
  height: 100%;
}

/* Force black background on vue-advanced-cropper layers */
.ism-cropper-canvas ::v-deep .vue-advanced-cropper__background,
.ism-cropper-canvas ::v-deep .vue-advanced-cropper__foreground {
  background: #000000 !important;
}

/* Overlay shadow around stencil */
.ism-cropper-canvas ::v-deep .vue-advanced-cropper__stencil {
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.48);
}

/* Stencil border — brand green */
.ism-cropper-canvas ::v-deep .vue-rectangle-stencil__preview,
.ism-cropper-canvas ::v-deep .vue-circle-stencil__preview {
  border: 2.5px solid var(--brand, #0D9972) !important;
  box-sizing: border-box;
}

/* ── Controls ────────────────────────────── */
.ism-cropper-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}
.ism-crop-ctrl-btn {
  width: 36px; height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s;
}
.ism-crop-ctrl-btn:hover {
  background: var(--brand-lt, rgba(13,153,114,0.08));
  color: var(--brand, #0D9972);
  border-color: var(--brand, #0D9972);
}

/* ── Footer ──────────────────────────────── */
.ism-cropper-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: #ffffff;
  border-top: 1px solid #eef2f7;
  flex-shrink: 0;
}
.ism-crop-btn-cancel {
  padding: 9px 18px;
  border-radius: 9px;
  border: 0;
  background: transparent;
  color: #64748b;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.18s;
}
.ism-crop-btn-cancel:hover {
  background: #f8fafc;
  color: #374151;
}
.ism-crop-btn-apply {
  display: inline-flex; align-items: center;
  padding: 9px 20px;
  border-radius: 9px; border: none;
  background: var(--brand, #0D9972);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(13, 153, 114, 0.32);
}
.ism-crop-btn-apply:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

@media (max-width: 600px) {
  .ism-cropper-body { height: 280px; }
  .ism-cropper-controls { flex-wrap: wrap; }
}
</style>
